<script setup lang="ts">
// Vendor
import { useScreens } from "vue-screen-utils";

// Utils
import { formatDate } from "@citizenm/foundation/utils/date";

// Constants
import {
  RATES_INPUT_DATE_FORMAT,
  RATES_DISPLAY_DATE_FORMAT,
} from "~/shared/constants";

const screen = useScreens();

const { locale } = useI18n();

const columns = computed(() => (screen.matches.md ? 2 : 1));
const rows = computed(() => (screen.matches.md ? 1 : 12));
const popover = computed(() => ({
  placement: screen.matches.md ? "bottom-end" : "auto",
  visibility: "click",
}));

let {
  placeholder = "dates",
  testAttribPrefix = "",
  singleDate = false,
} = defineProps<{
  placeholder?: string;
  testAttribPrefix?: string;
  singleDate?: boolean;
}>();

const today = new Date();
const tomorrow = new Date();
tomorrow.setDate(today.getDate() + 1);

const start = defineModel<Date | null>("start", {
  default: new Date(),
});
const end = defineModel<Date | null>("end", {
  default: new Date(),
});

const range = ref<Date | { start: Date; end: Date }>(
  singleDate
    ? (start.value as Date)
    : {
        start: start.value as Date,
        end: end.value as Date,
      },
);

watch(range, (val: Date | { start: Date; end: Date }) => {
  if (val instanceof Date) {
    start.value = val;
  } else {
    let endDate = val.end;

    if (val.end.getTime() === val.start.getTime()) {
      // Same day selected, make end date one day later
      endDate.setDate(endDate.getDate() + 1);

      // updated the component to show the updated range
      nextTick(() => {
        range.value = { start: val.start, end: endDate };
      });
    }

    start.value = val.start;
    end.value = endDate;
  }
});

const startDate = computed(() =>
  range.value instanceof Date ? range.value : range.value.start,
);

const formattedInputStartDate = computed(() =>
  formatDate(startDate.value, RATES_INPUT_DATE_FORMAT),
);

const endDate = computed(() =>
  range.value instanceof Date ? range.value : range.value.end,
);

const formattedInputEndDate = computed(() =>
  formatDate(endDate.value, RATES_INPUT_DATE_FORMAT),
);

const displayDate = computed(() => {
  if (singleDate) {
    return formatDate(startDate.value, RATES_DISPLAY_DATE_FORMAT, locale);
  }
  return `${formatDate(startDate.value, RATES_DISPLAY_DATE_FORMAT, locale)} - ${formatDate(endDate.value, RATES_DISPLAY_DATE_FORMAT, locale)}`;
});

const isRange = computed(() => !singleDate);
</script>

<template>
  <div class="citizem-calendar-wrap">
    <input type="hidden" name="startdate" :value="formattedInputStartDate" />
    <input type="hidden" name="enddate" :value="formattedInputEndDate" />
    <VDatePicker
      v-model="range"
      :columns="columns"
      :rows="rows"
      :popover="popover"
      :min-date="today"
      :is-range="isRange"
      :locale="locale"
      color="green"
      class="citizem-calendar max-w-full"
      :step="1"
    >
      <template #default="{ showPopover }">
        <Input
          v-bind="$attrs"
          :model-value="displayDate"
          :placeholder="placeholder"
          :id="`inp_${$.uid}`"
          class="input-date"
          aria-haspopup="dialog"
          readonly
          tabindex="0"
          :data-testid="`${testAttribPrefix}date-input`"
          @click="showPopover"
        >
          <template v-slot:after-icon>
            <IconCalendar />
          </template>
        </Input>
      </template>
    </VDatePicker>
  </div>
</template>

<style lang="scss">
.range-plugin {
  max-height: 80vh;
  overflow: auto;
}

@media (width <= 767px) {
  .citizem-calendar {
    margin-top: 60px;
  }
}

@media (width <= 480px) {
  .calendars .calendar:nth-child(n + 2) {
    display: block;
  }
}

.citizem-calendar-wrap {
  > .vc-popover-content-wrapper {
    &[data-popper-placement] {
      @include mdx {
        position: fixed !important;
        inset: 0 !important;
        width: 100%;
        height: 100%;
        overflow: auto;
        transform: none !important;
      }
    }
  }

  .vc-container {
    display: flex;

    // border: none !important;
    border-radius: var(--border-radius);

    // box-shadow: none;

    .vc-popover-caret {
      display: none;
    }

    &.vc-green {
      --gray-100: #f7fafc;
      --gray-200: #edf2f7;
      --gray-300: #e2e8f0;
      --gray-400: #cbd5e0;
      --gray-500: var(--primary-black);
      --gray-600: var(--primary-black);
      --gray-700: var(--primary-black);
      --gray-800: var(--primary-black);
      --gray-900: #1a202c;

      /*
      --green-100: var(--color-green);
      --green-200: var(--color-green);
      --green-300: var(--color-green);
      --green-400: var(--color-green);
      --green-500: var(--color-green);
      --green-600: var(--color-green);
      --green-700: var(--color-green);
      --green-800: var(--color-green);
      --green-900: var(--color-green);
      */

      --green-100: #f0fff4;
      --green-200: #c6f6d5;
      --green-300: #9ae6b4;
      --green-400: #68d391;
      --green-500: #48bb78;
      --green-600: var(--color-green);
      --green-700: #2f855a;
      --green-800: #276749;
      --green-900: #22543d;
      --shadow: var(--shadow);
      --shadow-inner: none;

      font-family: var(--font-family);
    }

    @include mdx {
      width: 100%;
    }
  }
}

.vc-popover-content {
  background: var(--primary-white);

  // box-shadow: var(--shadow);
  // border: 1px solid var(--color-border-hover) !important;
  &.direction-bottom {
    margin-top: var(--space-12);
  }

  @include mdx {
    margin: 0 !important;
    border: none !important;

    &.direction-bottom {
      margin: 0 !important;
    }

    &.direction-right {
      margin: 0 !important;
    }
  }
}

.citizem-calendar {
  .vc-header {
    padding: 0 var(--space-16);

    &.is-lg {
      font-size: var(--font-size);
    }
  }

  .vc-nav-popover-container {
    color: var(--primary-black);
  }

  .vc-nav-item {
    &.is-active {
      color: var(--primary-black);
    }

    &:hover {
      color: var(--primary-black);
      background-color: var(--color-message-green);
    }

    &.is-current {
      color: var(--color-green);
    }
  }

  .vc-day {
    min-height: 40px;
  }

  .vc-weeks {
    min-width: 300px;
  }
}

.input-date {
  display: flex;

  &.old {
    align-items: flex-end;

    .inp {
      font-size: var(--font-size);
      font-family: var(--font-family-bold);
      line-height: var(--line-height);
    }

    .input {
      + svg {
        margin-right: var(--space-6);
        margin-bottom: var(--space-8);
      }
    }
  }

  @include mdx {
    display: flex;
    width: 100%;
  }
}
</style>
